import { v4 as uuidv4 } from 'uuid'
import { createSlice } from '@reduxjs/toolkit'
import { checkInventoryStatus } from '../thunk/orderThunk'
import { INVENTORY_STATUS } from '@common/enums'

const initialState = {
    isLoading: false,
    error: null,

    items: [],
    tempItem: null,
    isConfirmPopupOpen: false,
    isUnsavedChanges: false,
    inventoryChanges: {
        soldOutItems: [], // Items removed due to being sold out
        checkAvailableItems: [], // Items that need availability confirmation
    },
    clientRequestId: null,
    creditToUseCents: 0, // Amount of credit to use for discount (in cents)
    isComplimentaryOrder: false, // Whether order is complimentary for Management role
    complimentaryOrderReason: '', // Reason for complimentary order

    // Assistant carts synced from PubSub, keyed by cartId (e.g., 'pos-1')
    assistantCarts: {
        // [cartId]: { items: [], totalsAfter: { amount: 0 }, version: 0 }
    },
    assistantInfo: {},
}

export const cartSlice = createSlice({
    name: 'cart',
    initialState,
    reducers: {
        // ----- Local cart reducers (existing) -----
        addItem: (state, action) => {
            const newItem = action.payload
            // Check for exact matches (same dishId, selection and variant)
            const existingItemIndex = state.items.findIndex(
                item =>
                    item.dishId === newItem.dishId &&
                    JSON.stringify(item.selections) === JSON.stringify(newItem.selections) &&
                    item.note === newItem.note
            )

            if (existingItemIndex !== -1) {
                // If exact match found (including variant), increase quantity
                const existingItemVariantIndex = state.items[existingItemIndex].variants.findIndex(
                    v => v.id === newItem.variants[0].id
                )

                if (existingItemVariantIndex !== -1) {
                    state.items[existingItemIndex].variants[existingItemVariantIndex].quantity += newItem.variants[0].quantity
                } else {
                    state.items[existingItemIndex].variants.push(newItem.variants[0])
                }
            } else {
                // If no exact match, add to cart
                state.items.push(newItem)
            }

            state.isUnsavedChanges = true
        },
        replaceItem: state => {
            if (state.tempItem) {
                state.items = state.items.map(item => (item.servingDate === state.tempItem.servingDate ? state.tempItem : item))
                state.tempItem = null
                state.isConfirmPopupOpen = false
            }
        },
        cancelReplace: state => {
            state.tempItem = null
            state.isConfirmPopupOpen = false
        },
        removeItem: (state, action) => {
            const idx = action.payload

            state.items = state.items.filter((_, i) => i !== idx)
        },
        removeVariant: (state, action) => {
            const { idx, variantId } = action.payload

            if (state.items[idx].variants.length === 1) {
                state.items = state.items.filter((_, i) => i !== idx)
            } else {
                state.items[idx].variants = state.items[idx].variants.filter(v => v.id !== variantId)
            }
        },
        clearCart: state => {
            state.items = []
            state.isConfirmPopupOpen = false
            state.isUnsavedChanges = false
            state.clientRequestId = null
            state.creditToUseCents = 0
            state.isComplimentaryOrder = false
            state.complimentaryOrderReason = ''
        },
        plusQuantity: (state, action) => {
            const { idx, variantId } = action.payload
            const item = state.items[idx].variants.find(v => v.id === variantId)
            if (item) {
                item.quantity++
            }
        },
        minusQuantity: (state, action) => {
            const { idx, variantId } = action.payload
            const item = state.items[idx].variants.find(v => v.id === variantId)
            if (item) {
                item.quantity = item.quantity > 1 ? item.quantity - 1 : 1
            }
        },
        clearInventoryChanges: state => {
            state.inventoryChanges = {
                soldOutItems: [],
                checkAvailableItems: [],
            }
        },
        initClientRequestId: state => {
            if (!state.clientRequestId) {
                state.clientRequestId = uuidv4()
            }
        },
        clearClientRequestId: state => {
            state.clientRequestId = null
        },

        // ----- Assistant carts (remote) reducers -----
        applyCartSnapshot: (state, action) => {
            const { cart } = action.payload || {}
            const cartId = cart.id
            if (!cartId) return
            const next = {
                items: Array.isArray(cart.items) ? cart.items.slice() : [],
                finalAmount: cart.summary?.finalAmount || 0,
                // Promo data from POS
                originAmount: cart.summary?.originAmount || null,
                promoDiscount: cart.summary?.promoDiscount || 0,
                eligiblePromos: Array.isArray(cart.summary?.eligiblePromos) ? cart.summary.eligiblePromos : [],
                version: cart.meta?.ts || Date.now(),
            }

            state.assistantInfo = cart.meta
            state.assistantCarts = { ...state.assistantCarts, [cartId]: next }
        },
        clearAssistantData: state => {
            state.assistantInfo = {}
            state.assistantCarts = {}
        },
        setCreditToUseCents: (state, action) => {
            state.creditToUseCents = action.payload
        },
        resetCredit: state => {
            // Unified action to reset credit usage
            // This can be called when cart items change, modal closes, etc.
            state.creditToUseCents = 0
        },
        setIsComplimentaryOrder: (state, action) => {
            state.isComplimentaryOrder = action.payload
            // Clear reason when disabling complimentary order
            if (!action.payload) {
                state.complimentaryOrderReason = ''
            }
            // Reset credit when complimentary order is toggled
            if (action.payload) {
                state.creditToUseCents = 0
            }
        },
        setComplimentaryOrderReason: (state, action) => {
            state.complimentaryOrderReason = action.payload
        },
    },
    extraReducers: builder => {
        builder
            /** checkInventoryStatus */
            .addCase(checkInventoryStatus.pending, state => {
                state.isLoading = true
                state.error = null
                // Clear previous inventory changes
                state.inventoryChanges = {
                    soldOutItems: [],
                    checkAvailableItems: [],
                }
            })
            .addCase(checkInventoryStatus.fulfilled, (state, action) => {
                const { inventoryStatus } = action.payload
                const { soldOutItems, checkAvailableItems } = state.inventoryChanges

                // Create a map for quick inventory status lookup
                const inventoryMap = new Map(inventoryStatus.map(inv => [inv.id, inv]))

                // Process each cart item
                state.items = state.items.filter(item => {
                    // Check variants
                    const variantsValid = item.variants.filter(variant => {
                        const inventory = inventoryMap.get(variant.inventory?.id)
                        if (inventory?.status === INVENTORY_STATUS.SOLD_OUT) {
                            soldOutItems.push({
                                itemName: item.name,
                                type: 'variant',
                                name: variant.name,
                                stallName: item.stallName,
                            })
                            return false
                        }
                        if (inventory?.status === INVENTORY_STATUS.CHECK_AVAILABLE) {
                            checkAvailableItems.push({
                                itemName: item.name,
                                type: 'variant',
                                name: variant.name,
                                stallName: item.stallName,
                            })
                        }
                        return true
                    })

                    // If no valid variants, remove the item
                    if (variantsValid.length === 0) {
                        return false
                    }
                    item.variants = variantsValid

                    // Check selections
                    item.selections = item.selections
                        .map(selection => {
                            const validOptions = selection.options.filter(option => {
                                const inventory = inventoryMap.get(option.inventory?.id)
                                if (inventory?.status === INVENTORY_STATUS.SOLD_OUT) {
                                    soldOutItems.push({
                                        itemName: item.name,
                                        stallName: item.stallName,
                                        type: 'option',
                                        selectionName: selection.name,
                                        name: option.name,
                                    })
                                    return false
                                }
                                if (inventory?.status === INVENTORY_STATUS.CHECK_AVAILABLE) {
                                    checkAvailableItems.push({
                                        itemName: item.name,
                                        stallName: item.stallName,
                                        type: 'option',
                                        selectionName: selection.name,
                                        name: option.name,
                                    })
                                }
                                return true
                            })
                            return {
                                ...selection,
                                options: validOptions,
                            }
                        })
                        .filter(selection => selection.options.length > 0)

                    return true
                })

                state.isLoading = false
            })
            .addCase(checkInventoryStatus.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload
            })
    },
})

export const cartActions = {
    ...cartSlice.actions,
    checkInventoryStatus,
}

export default cartSlice.reducer
